/**
 * Downlink commands for Milesight controllers, built from Milesight's published command tables
 * (support article "How to Remotely Control Devices via MQTT on Milesight Gateway" and the
 * UC51x Series Communication Protocol). Milesight's own encoder code is GPL-3.0 and is not
 * used here. Every command is confirmed only when the device reports the output in the new
 * state on a later uplink; nothing is shown as done just because it was sent.
 *
 *   UC300  DO1/DO2:        07|08  state  ff                 (Class C: acts at once)
 *   UC50x  GPIO1/GPIO2:    03|04  state  00  ff             (Class C)
 *   UC51x  valve 1/2:      ff 1d  control  sequence [time (3 B, LE s)] [pulses (4 B, LE)]
 *            control bits: 7 time on · 6 pulse-count on · 5 open/close · 1-0 valve (00 / 01)
 *            (usually Class A: the command waits for the device's next uplink)
 *
 * Milesight devices take downlinks on fPort 85 unless changed in ToolBox.
 */
import type { DecodedValue } from "./milesight.ts";
import { QOTO_MODEL, setQotoValve } from "./qoto-commands.ts";

export const MILESIGHT_DOWNLINK_PORT = 85;

/** A binary downlink with how to tell it worked from a decoded uplink. */
export interface MilesightCommand {
  /** The bytes as hex, for the command log. */
  text: string;
  bytes: Uint8Array;
  fPort: number;
  /** True when the maker gives no port: send on the one the device last reported on. */
  fPortFromUplink?: boolean;
  description: string;
  /** The output port the command drives, in decoder channel names (do_1, gpio_out_2, valve_1). */
  port: string;
  /** True when the decoded uplink shows the output in the commanded state. */
  confirmDecoded: (values: readonly DecodedValue[]) => boolean;
  /** How long to wait for that uplink before resending: longer for Class A devices. */
  confirmTimeoutMs: number;
}

const MINUTE = 60_000;

export interface MilesightOutput {
  /** The decoder channel name of the output. */
  port: string;
  label: string;
  /** "valve" outputs can be opened for a set time or number of pulses; "qoto" valves to a percentage. */
  kind: "do" | "gpio" | "valve" | "qoto";
  channel: 1 | 2;
}

/** The switchable outputs a Milesight controller model has, by decoder channel name. */
export function milesightOutputs(model: string): MilesightOutput[] {
  switch (model) {
    case "uc300":
      return [
        { port: "do_1", label: "DO1", kind: "do", channel: 1 },
        { port: "do_2", label: "DO2", kind: "do", channel: 2 },
      ];
    case "uc501":
      return [
        { port: "gpio_out_1", label: "GPIO1", kind: "gpio", channel: 1 },
        { port: "gpio_out_2", label: "GPIO2", kind: "gpio", channel: 2 },
      ];
    case "uc511":
      // The decoder's "uc511" covers the UC511 (one valve) and UC512 (two): hide valve 2 in set-up on a UC511.
      return [
        { port: "valve_1", label: "Valve 1", kind: "valve", channel: 1 },
        { port: "valve_2", label: "Valve 2", kind: "valve", channel: 2 },
      ];
    case QOTO_MODEL:
      return [{ port: "valve", label: "Valve", kind: "qoto", channel: 1 }];
    default:
      return [];
  }
}

/** True when this model's output can be switched from Think-Control. */
export function isMilesightOutput(model: string, port: string): boolean {
  return milesightOutputs(model).some((o) => o.port === port);
}

export interface ValveOptions {
  /** Open for this many seconds, then the valve closes itself (1 .. 16,777,215). */
  seconds?: number;
  /** Open until the flow meter on the valve counts this many pulses (1 .. 4,294,967,295). */
  pulses?: number;
  /** Command sequence 0-255; the UC51x ignores a repeat of the last sequence number. */
  sequence?: number;
  /** QOTO valves: how far to open, 1-100 % (default fully). */
  percent?: number;
  /** QOTO three-way valves: open towards direction A. */
  directionA?: boolean;
  /** QOTO: the command number the valve echoes back (default: the time, so every command is new). */
  mission?: number;
}

/**
 * Switch an output on a Milesight controller. Throws when the model has no such output.
 * For valves, `options` opens for a set time or pulse count (closing takes neither).
 */
export function setMilesightOutput(model: string, port: string, on: boolean, options: ValveOptions = {}): MilesightCommand {
  const out = milesightOutputs(model).find((o) => o.port === port);
  if (!out) throw new Error(`${model} has no switchable output ${port}`);
  const onOff = on ? "on" : "off";
  const confirmDecoded = (values: readonly DecodedValue[]) => values.some((v) => v.name === port && (v.value !== 0) === on);
  switch (out.kind) {
    case "do":
      return {
        text: hex([0x06 + out.channel, on ? 1 : 0, 0xff]),
        bytes: Uint8Array.from([0x06 + out.channel, on ? 1 : 0, 0xff]),
        fPort: MILESIGHT_DOWNLINK_PORT,
        description: `${out.label} ${onOff}`,
        port,
        confirmDecoded,
        confirmTimeoutMs: 2 * MINUTE,
      };
    case "gpio":
      return {
        text: hex([0x02 + out.channel, on ? 1 : 0, 0x00, 0xff]),
        bytes: Uint8Array.from([0x02 + out.channel, on ? 1 : 0, 0x00, 0xff]),
        fPort: MILESIGHT_DOWNLINK_PORT,
        description: `${out.label} ${onOff}`,
        port,
        confirmDecoded,
        confirmTimeoutMs: 2 * MINUTE,
      };
    case "qoto":
      return setQotoValve(on, {
        ...(options.percent !== undefined ? { percent: options.percent } : {}),
        ...(options.directionA !== undefined ? { directionA: options.directionA } : {}),
        mission: options.mission ?? Math.floor(Date.now() / 1000) >>> 0,
      });
    case "valve": {
      const seconds = on ? options.seconds : undefined;
      const pulses = on ? options.pulses : undefined;
      if (seconds !== undefined && (!Number.isInteger(seconds) || seconds < 1 || seconds > 0xffffff)) throw new Error("seconds must be 1 to 16,777,215");
      if (pulses !== undefined && (!Number.isInteger(pulses) || pulses < 1 || pulses > 0xffffffff)) throw new Error("pulses must be 1 to 4,294,967,295");
      const seq = options.sequence ?? 0;
      if (!Number.isInteger(seq) || seq < 0 || seq > 0xff) throw new Error("sequence must be 0 to 255");
      const control = (seconds !== undefined ? 0x80 : 0) | (pulses !== undefined ? 0x40 : 0) | (on ? 0x20 : 0) | (out.channel - 1);
      const bytes = [0xff, 0x1d, control, seq];
      if (seconds !== undefined) bytes.push(seconds & 0xff, (seconds >> 8) & 0xff, (seconds >> 16) & 0xff);
      if (pulses !== undefined) bytes.push(pulses & 0xff, (pulses >>> 8) & 0xff, (pulses >>> 16) & 0xff, (pulses >>> 24) & 0xff);
      const how = [seconds !== undefined ? `for ${seconds} s` : "", pulses !== undefined ? `until ${pulses} pulses` : ""].filter(Boolean).join(" or ");
      return {
        text: hex(bytes),
        bytes: Uint8Array.from(bytes),
        fPort: MILESIGHT_DOWNLINK_PORT,
        description: `${out.label} ${on ? "open" : "close"}${how ? ` ${how}` : ""}`,
        port,
        confirmDecoded,
        // Valve controllers are usually Class A: the command goes out after the device's next
        // report, which can be many minutes away.
        confirmTimeoutMs: 20 * MINUTE,
      };
    }
  }
}

function hex(bytes: number[]): string {
  return bytes.map((b) => b.toString(16).padStart(2, "0")).join("");
}
